/**
 * ConversionDetailView Component
 *
 * Shows property details with associated deal/agreement information.
 * Used in the Conversions tab to display matched properties with their deals.
 *
 * @param {Object} property - Property data object
 * @param {Object} deal - Deal/agreement data object
 * @param {Array} transactions - Transaction history array (optional)
 * @param {boolean} showMap - Whether to show the map (default: true)
 * @param {Function} onClose - Optional close handler
 */

import React from "react";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import {
  FaHome,
  FaBed,
  FaBath,
  FaPoundSign,
  FaMapMarkerAlt,
  FaBuilding,
  FaKey,
  FaHandshake,
  FaChartLine,
  FaClock,
  FaCheckCircle,
  FaCalendar,
  FaExternalLinkAlt,
} from "react-icons/fa";
import BaseMap from "../../map/BaseMap";
import { Marker } from "@react-google-maps/api";
import DeliveryStatusBadge from "../DeliveryStatusBadge";
import GeocodeButton from "../GeocodeButton";
import "./ConversionDetailView.scss";

export const ConversionDetailView = ({
  property,
  deal,
  transactions = [],
  showMap = true,
  onClose,
}) => {
  const [coordinates, setCoordinates] = React.useState({
    lat: property?.latitude,
    lng: property?.longitude,
  });
  if (!property) {
    return (
      <div className="conversion-detail-view conversion-detail-view--empty">
        <div className="empty-state">
          <FaHome />
          <p>Property not found</p>
        </div>
      </div>
    );
  }

  // Format helpers
  const formatPrice = (price) => {
    if (!price) return "N/A";
    if (typeof price === "string" && price.includes("£")) {
      return price;
    }
    return `£${Number(price).toLocaleString()}`;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // Map configuration
  const hasLocation = coordinates.lat && coordinates.lng;
  const mapCenter = hasLocation ? coordinates : { lat: 52.27, lng: -1.87 }; // UK center fallback

  const handleGeocodeSuccess = (data) => {
    setCoordinates({
      lat: data.latitude,
      lng: data.longitude,
    });
  };

  return (
    <div className="conversion-detail-view">
      <div className="conversion-detail-view__container">
        {/* Left Panel - Property & Deal Details */}
        <div className="conversion-detail-view__details">
          {/* Property Header */}
          <div className="detail-header">
            <div className="detail-header__title">
              <FaHome className="detail-header__icon" />
              <div>
                <h2>{property.address}</h2>
                <p className="detail-header__subtitle">{property.postcode}</p>
              </div>
            </div>
            <div className="detail-header__price">
              {formatPrice(property.price)}
            </div>
          </div>

          {/* Quick Stats */}
          <div className="detail-stats">
            {property.bedrooms && (
              <div className="detail-stat">
                <FaBed className="detail-stat__icon" />
                <span>
                  {property.bedrooms} bed{property.bedrooms !== 1 ? "s" : ""}
                </span>
              </div>
            )}
            {property.bathrooms && (
              <div className="detail-stat">
                <FaBath className="detail-stat__icon" />
                <span>
                  {property.bathrooms} bath{property.bathrooms !== 1 ? "s" : ""}
                </span>
              </div>
            )}
            {property.propertyType && (
              <div className="detail-stat">
                <FaBuilding className="detail-stat__icon" />
                <span>{property.propertyType}</span>
              </div>
            )}
            {property.tenure && (
              <div className="detail-stat">
                <FaKey className="detail-stat__icon" />
                <span>{property.tenure}</span>
              </div>
            )}
          </div>

          {/* Property Summary Section */}
          <div className="detail-section">
            <h3 className="detail-section__title">
              <FaHome className="section-icon" />
              Property Details
            </h3>
            <div className="detail-grid">
              {property.propertyStatus && (
                <div className="detail-item">
                  <span className="detail-item__label">Status</span>
                  <span className="detail-item__value">
                    {property.propertyStatus}
                  </span>
                </div>
              )}
              {property.propertyType && (
                <div className="detail-item">
                  <span className="detail-item__label">Type</span>
                  <span className="detail-item__value">
                    {property.propertyType}
                  </span>
                </div>
              )}
              {property.bedrooms && (
                <div className="detail-item">
                  <span className="detail-item__label">Bedrooms</span>
                  <span className="detail-item__value">
                    {property.bedrooms}
                  </span>
                </div>
              )}
              {property.bathrooms && (
                <div className="detail-item">
                  <span className="detail-item__label">Bathrooms</span>
                  <span className="detail-item__value">
                    {property.bathrooms}
                  </span>
                </div>
              )}
              {property.propertyId && (
                <div className="detail-item">
                  <span className="detail-item__label">Property ID</span>
                  <span className="detail-item__value">
                    <Link
                      to={`/dashboard/properties/${property.id}`}
                      className="detail-link"
                    >
                      {property.propertyId}
                      <FaExternalLinkAlt className="detail-link__icon" />
                    </Link>
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Deal/Agreement Section */}
          {deal && (
            <div className="detail-section detail-section--highlight">
              <h3 className="detail-section__title">
                <FaHandshake className="section-icon" />
                Deal Information
              </h3>
              <div className="detail-grid">
                {deal.linkedDealId && (
                  <div className="detail-item">
                    <span className="detail-item__label">Deal ID</span>
                    <span className="detail-item__value">
                      {deal.linkedDealId}
                    </span>
                  </div>
                )}
                {deal.linkedDealStage && (
                  <div className="detail-item">
                    <span className="detail-item__label">Stage</span>
                    <span className="detail-item__value deal-stage">
                      <FaChartLine className="detail-item__icon" />
                      {deal.linkedDealStage}
                    </span>
                  </div>
                )}
                {deal.linkedDealValue && (
                  <div className="detail-item">
                    <span className="detail-item__label">Deal Value</span>
                    <span className="detail-item__value detail-item__value--highlight">
                      {formatPrice(deal.linkedDealValue)}
                    </span>
                  </div>
                )}
                {deal.daysToQuote !== undefined && (
                  <div className="detail-item">
                    <span className="detail-item__label">Days to Quote</span>
                    <span className="detail-item__value">
                      <FaClock className="detail-item__icon" />
                      {deal.daysToQuote} days
                    </span>
                  </div>
                )}
                {deal.dealMatchedAt && (
                  <div className="detail-item">
                    <span className="detail-item__label">Matched</span>
                    <span className="detail-item__value">
                      <FaCheckCircle className="detail-item__icon detail-item__icon--success" />
                      {formatDate(deal.dealMatchedAt)}
                    </span>
                  </div>
                )}
                {deal.dealOwner && (
                  <div className="detail-item">
                    <span className="detail-item__label">Deal Owner</span>
                    <span className="detail-item__value">{deal.dealOwner}</span>
                  </div>
                )}
                {deal.dealAmount && (
                  <div className="detail-item">
                    <span className="detail-item__label">Amount</span>
                    <span className="detail-item__value detail-item__value--highlight">
                      {formatPrice(deal.dealAmount)}
                    </span>
                  </div>
                )}
                {deal.dealCloseDate && (
                  <div className="detail-item">
                    <span className="detail-item__label">Close Date</span>
                    <span className="detail-item__value">
                      <FaCalendar className="detail-item__icon" />
                      {formatDate(deal.dealCloseDate)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Delivery Status */}
          {property.deliveryStatus && (
            <div className="detail-section">
              <h3 className="detail-section__title">
                <FaCheckCircle className="section-icon" />
                Delivery Status
              </h3>
              <DeliveryStatusBadge
                status={property.deliveryStatus}
                size="large"
                showLabel
              />
            </div>
          )}

          {/* Location */}
          {property.address && (
            <div className="detail-section">
              <h3 className="detail-section__title">
                <FaMapMarkerAlt className="section-icon" />
                Location
              </h3>
              <div className="detail-grid">
                <div className="detail-item detail-item--full">
                  <span className="detail-item__label">Address</span>
                  <span className="detail-item__value">{property.address}</span>
                </div>
                {property.postcode && (
                  <div className="detail-item">
                    <span className="detail-item__label">Postcode</span>
                    <span className="detail-item__value">
                      {property.postcode}
                    </span>
                  </div>
                )}
                {hasLocation && (
                  <>
                    <div className="detail-item">
                      <span className="detail-item__label">Latitude</span>
                      <span className="detail-item__value">
                        {property.latitude.toFixed(6)}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-item__label">Longitude</span>
                      <span className="detail-item__value">
                        {property.longitude.toFixed(6)}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Right Panel - Map */}
        {showMap && (
          <div className="conversion-detail-view__map">
            {hasLocation ? (
              <BaseMap center={mapCenter} zoom={15}>
                <Marker
                  position={mapCenter}
                  title={property.address}
                />
              </BaseMap>
            ) : (
              <GeocodeButton
                propertyId={property.id}
                address={property.address}
                postcode={property.postcode}
                onSuccess={handleGeocodeSuccess}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

ConversionDetailView.propTypes = {
  property: PropTypes.object.isRequired,
  deal: PropTypes.object,
  transactions: PropTypes.array,
  showMap: PropTypes.bool,
  onClose: PropTypes.func,
};

export default ConversionDetailView;
